/**
 * /apps/:appId/users — unified Users page.
 *
 * Merges the previous "Members & Invites" page with the new role-assignment
 * concept. One row per platform user who's a member of this app. Each row
 * shows:
 *
 *   - identity (name / email)
 *   - ACCESS role: super_admin / admin / member  — gates what you can do
 *                  in the app
 *   - ORG roles: chips of titles assigned to that user (CTO, EM, ...) —
 *                inline +/- editing for admins
 *   - joined date
 *   - actions (Remove / Leave app)
 *
 * Invitations panel sits below for admins.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { Search } from 'lucide-react';
import Modal from '../../components/Modal';
import ConfirmDeleteModal from '../../components/workspace/ConfirmDeleteModal';
import { useApi } from '../../lib/api';
import { useWorkspace } from '../../lib/workspace';
import { accessRoleLabel, formatDate, memberDisplayName } from '../../lib/format';

// Module-level cache so navigating back to Users is instant.
const usersCache = new Map(); // appId → { members, invitations, assignments, allRoles }

// Turn an API error into a user-facing string. For the "has_reportees" hard
// block (deactivate / remove-role while a team still reports to this person),
// append the reportee names so the Admin knows exactly who to reassign first.
function reporteeError(err) {
  const data = err?.response?.data;
  if (data?.code === 'has_reportees') {
    const names = (data.reportees || []).map((r) => r.name).filter(Boolean);
    return names.length ? `${data.error} Reportees: ${names.join(', ')}.` : data.error;
  }
  return data?.error || err?.message || 'Something went wrong';
}

export default function Users() {
  const { appId } = useParams();
  const api = useApi();
  const apiRef = useRef(api);
  apiRef.current = api;
  const { me, canWrite, canManage, searchQuery } = useWorkspace();
  const { user: clerkUser } = useUser(); // Clerk identity — used when DB has no data for self

  // Clerk returns a new object reference every render — use refs so load()
  // doesn't change identity and retrigger the useEffect on every render.
  const meRef = useRef(me);
  meRef.current = me;
  const clerkUserRef = useRef(clerkUser);
  clerkUserRef.current = clerkUser;
  const canWriteRef = useRef(canWrite);
  canWriteRef.current = canWrite;

  const cached = usersCache.get(appId);
  const [members, setMembers]         = useState(cached?.members     || []);
  const [invitations, setInvitations] = useState(cached?.invitations || []);
  const [assignments, setAssignments] = useState(cached?.assignments || []);
  const [allRoles, setAllRoles]       = useState(cached?.allRoles    || []);
  const [loading, setLoading]         = useState(!cached);
  const [error, setError]             = useState(null);

  const [inviteOpen, setInviteOpen]         = useState(false);
  const [removeTarget, setRemoveTarget]     = useState(null);
  const [reactivateTarget, setReactivateTarget] = useState(null);
  const [editTarget, setEditTarget]         = useState(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const [m, inv, ra, rs] = await Promise.all([
        apiRef.current.listAppMembers(appId, { includeInactive: true }),
        canWriteRef.current ? apiRef.current.listAppInvitations(appId) : Promise.resolve({ items: [] }),
        apiRef.current.listRoleAssignments(appId).catch(() => ({ items: [] })),
        apiRef.current.roles.list(appId).catch(() => ({ items: [] })),
      ]);
      const memberList   = m.items  || [];
      const invList      = inv.items || [];
      const assignList   = ra.items  || [];
      const roleList     = rs.items  || [];
      setMembers(memberList);
      setInvitations(invList);
      setAssignments(assignList);
      setAllRoles(roleList);
      usersCache.set(appId, { members: memberList, invitations: invList, assignments: assignList, allRoles: roleList });

      // Auto-sync: if the current user's DB row has no email or name, push Clerk data.
      const me = meRef.current;
      const clerkUser = clerkUserRef.current;
      if (me && clerkUser) {
        const selfRow = memberList.find((r) => r.user_id === me.id);
        if (!selfRow?.email || !selfRow?.first_name) {
          const clerkEmail = clerkUser.emailAddresses?.[0]?.emailAddress;
          const clerkFirst = clerkUser.firstName;
          const clerkLast  = clerkUser.lastName;
          if (clerkEmail || clerkFirst) {
            apiRef.current.syncMe({ email: clerkEmail, firstName: clerkFirst || '', lastName: clerkLast || '' })
              .catch(() => {});
          }
        }
      }
    } catch (err) {
      if (!silent) setError(err.response?.data?.error || err.message || 'Failed to load');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [appId]);

  useEffect(() => {
    load(!!cached);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  // ---- access-role ----
  const handleAccessRoleChange = async (userId, role) => {
    setError(null);
    try {
      await api.changeMemberRole(appId, userId, role);
      await load(true);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const handleRemove = async () => {
    if (!removeTarget) return;
    try {
      await api.removeMember(appId, removeTarget.user_id);
      setRemoveTarget(null);
      await load(true);
    } catch (err) {
      setRemoveTarget(null);
      setError(reporteeError(err));
    }
  };

  const handleReactivate = async () => {
    if (!reactivateTarget) return;
    await api.reactivateMember(appId, reactivateTarget.user_id);
    setReactivateTarget(null);
    await load(true);
  };

  const handleAssignRole = async (userId, roleId) => {
    setError(null);
    try {
      await api.assignRole(appId, { platform_user_id: userId, role_id: roleId });
      await load(true);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const handleUnassignRole = async (assignmentId) => {
    setError(null);
    try {
      await api.removeRoleAssignment(appId, assignmentId);
      await load(true);
    } catch (err) {
      setError(reporteeError(err));
    }
  };

  const handleRevokeInvitation = async (id) => {
    try {
      await api.revokeInvitation(appId, id);
      await load(true);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  if (loading) return <p className="muted" style={{ padding: 24 }}>Loading users…</p>;

  // Split members into active and deactivated, filtered by top navbar search
  const q = (searchQuery || '').toLowerCase().trim();
  const matchesMember = (m) => {
    if (!q) return true;
    const name = memberDisplayName(m.first_name || '', m.last_name || '', m.email || '').toLowerCase();
    return name.includes(q) || (m.email || '').toLowerCase().includes(q);
  };
  const activeMembers      = members.filter((m) => m.is_active !== 0 && matchesMember(m));
  const deactivatedMembers = members.filter((m) => m.is_active === 0 && matchesMember(m));
  const pendingInvitations = invitations.filter((inv) => inv.status === 'pending');

  // Index assignments by user id for quick row rendering.
  const assignmentsByUser = new Map();
  for (const a of assignments) {
    if (!a.user) continue;
    if (!assignmentsByUser.has(a.platform_user_id)) {
      assignmentsByUser.set(a.platform_user_id, []);
    }
    assignmentsByUser.get(a.platform_user_id).push(a);
  }

  return (
    <>
      {error && <div className="banner banner-error">{error}</div>}

      <section className="entity-section">
        <div className="entity-head">
          <h1 className="page-title">Users</h1>
          {canWrite && (
            <button className="btn-primary" onClick={() => setInviteOpen(true)}>
              + Invite
            </button>
          )}
        </div>

        <p className="page-subtitle">
          People with access to this workspace. Each user has an
          <strong> access role</strong> (governs what they can edit) and may
          hold one or more <strong>organisation roles</strong> (CTO, EM, …).
        </p>

        <div className="entity-table-wrap">
          <table className="entity-table users-table">
            <thead>
              <tr>
                <th style={{ width: 40 }}>Sl No</th>
                <th>Name / Email</th>
                <th>Access</th>
                <th>Organisation roles</th>
                <th>Joined</th>
                <th style={{ width: 1 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {activeMembers.map((m, idx) => {
                const isOwner = !!m.is_owner;
                const isMe = !!me && m.user_id === me.id;
                const myAssignments = assignmentsByUser.get(m.user_id) || [];

                // For the current user, fall back to Clerk's live data when the
                // DB row has no email / name (JWT template not configured to
                // include those claims — see POST /api/me/sync for the DB fix).
                const rowFirst = m.first_name || (isMe ? clerkUser?.firstName  : null) || '';
                const rowLast  = m.last_name  || (isMe ? clerkUser?.lastName   : null) || '';
                const rowEmail = m.email      || (isMe ? clerkUser?.emailAddresses?.[0]?.emailAddress : null) || '';

                return (
                  <tr key={m.membership_id}>
                    <td className="muted small" style={{ textAlign: 'center' }}>{idx + 1}</td>
                    <td style={{ maxWidth: 220, overflow: 'hidden' }}>
                      <strong
                        title={memberDisplayName(rowFirst, rowLast, rowEmail)}
                        style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {memberDisplayName(rowFirst, rowLast, rowEmail)}
                        {isMe && <span className="muted small"> · you</span>}
                      </strong>
                      {/* Only show email sub-line when there's a real name above it */}
                      {(rowFirst || rowLast) && rowEmail && (
                        <div className="muted" title={rowEmail} style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{rowEmail}</div>
                      )}
                    </td>
                    <td>
                      {isOwner || !canManage ? (
                        <span className={`app-role-badge role-${m.role}`}>
                          {accessRoleLabel(m.role)}
                        </span>
                      ) : (
                        <select
                          className="form-input small"
                          style={{ width: 120 }}
                          size={1}
                          value={m.role}
                          onChange={(e) => handleAccessRoleChange(m.user_id, e.target.value)}
                        >
                          <option value="admin">Admin</option>
                          <option value="member">Member</option>
                        </select>
                      )}
                    </td>
                    <td>
                      <OrgRolesCell
                        userId={m.user_id}
                        assignments={myAssignments}
                        allRoles={allRoles}
                        canEdit={canWrite}
                        onAssign={(roleId) => handleAssignRole(m.user_id, roleId)}
                        onUnassign={handleUnassignRole}
                      />
                    </td>
                    <td className="muted small">{formatDate(m.joined_at)}</td>
                    <td className="row-actions">
                      {canWrite && (!isOwner || isMe) && (
                        <button
                          className="btn-link-sm"
                          style={{ marginRight: 6 }}
                          onClick={() => setEditTarget(m)}
                        >
                          Edit
                        </button>
                      )}
                      {isOwner ? (
                        <span className="muted">—</span>
                      ) : canManage || isMe ? (
                        <button
                          className={isMe ? 'btn-link-sm danger' : 'btn-remove'}
                          onClick={() => setRemoveTarget(m)}
                        >
                          {isMe ? 'Leave app' : 'Remove'}
                        </button>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {deactivatedMembers.length > 0 && (
        <section className="entity-section" style={{ marginTop: 24 }}>
          <div className="entity-head">
            <h2 className="page-title-sm" style={{ color: '#888' }}>Deactivated Members</h2>
          </div>
          <div className="entity-table-wrap">
            <table className="entity-table users-table">
              <thead>
                <tr>
                  <th style={{ width: 40 }}>Sl No</th>
                  <th>Name / Email</th>
                  <th>Organisation roles</th>
                  <th>Joined</th>
                  {canManage && <th style={{ width: 1 }}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {deactivatedMembers.map((m, idx) => {
                  const rowFirst = m.first_name || '';
                  const rowLast  = m.last_name  || '';
                  const rowEmail = m.email      || '';
                  const myAssignments = assignmentsByUser.get(m.user_id) || [];
                  return (
                    <tr key={m.membership_id} style={{ opacity: 0.65 }}>
                      <td className="muted small" style={{ textAlign: 'center' }}>{idx + 1}</td>
                      <td style={{ maxWidth: 220, overflow: 'hidden' }}>
                        <strong title={memberDisplayName(rowFirst, rowLast, rowEmail)} style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{memberDisplayName(rowFirst, rowLast, rowEmail)}</strong>
                        {(rowFirst || rowLast) && rowEmail && (
                          <div className="muted" title={rowEmail} style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{rowEmail}</div>
                        )}
                      </td>
                      <td>
                        {myAssignments.length === 0
                          ? <span className="muted small">— none —</span>
                          : myAssignments.map((a) => (
                            <span key={a.id} className="org-role-chip">{a.role?.title || `Role #${a.role_id}`}</span>
                          ))}
                      </td>
                      <td className="muted small">{formatDate(m.joined_at)}</td>
                      {canManage && (
                        <td className="row-actions">
                          <button
                            className="btn-link-sm activate"
                            onClick={() => setReactivateTarget(m)}
                          >
                            Activate
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {canWrite && (
        <section className="entity-section">
          <h2 className="page-title">Pending Invitations</h2>
          {pendingInvitations.length === 0 ? (
            <p className="muted">No pending invitations.</p>
          ) : (
            <div className="entity-table-wrap">
              <table className="entity-table">
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>Sl No</th>
                    <th>Email</th>
                    <th>Access Role</th>
                    <th>Sent</th>
                    <th>Invited by</th>
                    <th style={{ width: 1 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingInvitations.map((inv, idx) => (
                    <tr key={inv.id}>
                      <td className="muted small" style={{ textAlign: 'center' }}>{idx + 1}</td>
                      <td>{inv.invited_email}</td>
                      <td>{accessRoleLabel(inv.role)}</td>
                      <td className="muted small">{formatDate(inv.created_at)}</td>
                      <td className="muted small">{inv.invited_by_email}</td>
                      <td className="row-actions">
                        <button
                          className="btn-link-sm danger"
                          onClick={() => handleRevokeInvitation(inv.id)}
                        >
                          Revoke
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      <EditUserModal
        open={!!editTarget}
        member={editTarget}
        canWrite={canWrite}
        onClose={() => setEditTarget(null)}
        onSubmit={async (body) => {
          await api.updateMemberProfile(appId, editTarget.user_id, body);
          await load(true);
        }}
      />

      <InviteModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onSubmit={async (body) => {
          await api.createInvitation(appId, body);
          await load(true);
        }}
      />

      <ConfirmDeleteModal
        open={!!removeTarget}
        title={
          removeTarget && me && removeTarget.user_id === me.id
            ? 'Leave this app?'
            : 'Deactivate member?'
        }
        message={
          removeTarget && me && removeTarget.user_id === me.id
            ? 'You will lose access to this workspace immediately.'
            : 'They will be deactivated and removed from the active list. You can reactivate them later.'
        }
        confirmLabel={
          removeTarget && me && removeTarget.user_id === me.id
            ? 'Leave'
            : 'Deactivate'
        }
        onConfirm={handleRemove}
        onClose={() => setRemoveTarget(null)}
      />

      <ConfirmDeleteModal
        open={!!reactivateTarget}
        title="Reactivate member?"
        message={`${reactivateTarget ? memberDisplayName(reactivateTarget.first_name, reactivateTarget.last_name, reactivateTarget.email) : 'This user'} will regain access to the workspace.`}
        confirmLabel="Reactivate"
        onConfirm={handleReactivate}
        onClose={() => setReactivateTarget(null)}
      />
    </>
  );
}

// =========================================================================
// OrgRolesCell — chips of currently-assigned org roles + "Manage" button.
// =========================================================================
function OrgRolesCell({ userId, assignments, allRoles, canEdit, onAssign, onUnassign }) {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <div className="org-roles-cell">
      {assignments.length === 0 ? (
        <span className="muted small">— none —</span>
      ) : (
        assignments.map((a) => (
          <span key={a.id} className="org-role-chip">
            {a.role?.title || `Role #${a.role_id}`}
          </span>
        ))
      )}
      {canEdit && (
        <>
          <button
            className="btn-link-sm small"
            style={{ marginLeft: 4 }}
            onClick={() => setModalOpen(true)}
            title="Add or remove roles for this user"
          >
            {assignments.length === 0 ? '+ Assign Roles' : '✎ Manage'}
          </button>
          <ManageRolesModal
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            allRoles={allRoles}
            assignments={assignments}
            onAssign={onAssign}
            onUnassign={onUnassign}
          />
        </>
      )}
    </div>
  );
}

// =========================================================================
// ManageRolesModal — checkbox list; assign/unassign any combination at once.
// =========================================================================
function ManageRolesModal({ open, onClose, allRoles, assignments, onAssign, onUnassign }) {
  const assignedRoleIds = new Set(assignments.map((a) => a.role_id));

  // Local checked state — initialise from current assignments each time modal opens
  const [checked, setChecked] = useState(() => new Set(assignedRoleIds));
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState(null);
  const [query,   setQuery]   = useState('');

  // Use a ref so the reset only fires when the modal opens, not when
  // assignments reference changes due to a background data refresh.
  const assignmentsRef = React.useRef(assignments);
  assignmentsRef.current = assignments;

  React.useEffect(() => {
    if (open) {
      setChecked(new Set(assignmentsRef.current.map((a) => a.role_id)));
      setError(null);
      setSaving(false);
      setQuery('');
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const visibleRoles = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allRoles;
    return allRoles.filter((role) =>
      [role.title, role.code].some((v) => String(v || '').toLowerCase().includes(q)),
    );
  }, [allRoles, query]);

  const toggle = (roleId) => {
    setChecked((prev) => {
      const next = new Set(prev);
      next.has(roleId) ? next.delete(roleId) : next.add(roleId);
      return next;
    });
  };

  const handleSave = async () => {
    setError(null);
    setSaving(true);
    try {
      // Roles to add = checked but not currently assigned
      const toAdd    = [...checked].filter((id) => !assignedRoleIds.has(id));
      // Roles to remove = currently assigned but unchecked
      const toRemove = assignments.filter((a) => !checked.has(a.role_id));

      // Run all adds/removes in parallel
      await Promise.all([
        ...toAdd.map((roleId) => onAssign(roleId)),
        ...toRemove.map((a)    => onUnassign(a.id)),
      ]);
      onClose();
    } catch (err) {
      setError(err?.message || 'Failed to save role changes');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges =
    [...checked].some((id) => !assignedRoleIds.has(id)) ||
    assignments.some((a) => !checked.has(a.role_id));

  return (
    <Modal open={open} title="Manage Organisation Roles" onClose={saving ? undefined : onClose}>
      <div className="manage-roles-modal">
        <p className="muted" style={{ marginBottom: 14 }}>
          Check all the roles this user should hold. You can assign multiple roles.
        </p>

        {allRoles.length === 0 ? (
          <p className="muted">No org roles defined yet. Create roles first.</p>
        ) : (
          <>
            <div className="tl-search-wrap" style={{ marginBottom: 10 }}>
              <Search className="tl-search-icon" size={14} />
              <input
                className="tl-search-input"
                placeholder="Search roles…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                disabled={saving}
              />
              {query && (
                <button className="tl-search-clear" onClick={() => setQuery('')}>×</button>
              )}
            </div>

            {visibleRoles.length === 0 ? (
              <p
                className="muted tl-no-match"
                style={{
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                  whiteSpace: 'normal',
                  marginRight: 0,
                }}
              >
                No roles match “{query}”.
              </p>
            ) : (
              <ul className="manage-roles-list">
                {visibleRoles.map((role) => (
                  <li key={role.id} className="manage-roles-item">
                    <label className="manage-roles-label">
                      <input
                        type="checkbox"
                        checked={checked.has(role.id)}
                        onChange={() => toggle(role.id)}
                        disabled={saving}
                      />
                      <span className="manage-roles-title">{role.title}</span>
                      {role.code && (
                        <span className="muted small manage-roles-code">{role.code}</span>
                      )}
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}

        {error && <div className="form-error" style={{ marginTop: 10 }}>{error}</div>}

        <div className="form-actions" style={{ marginTop: 16 }}>
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={handleSave}
            disabled={saving || !hasChanges}
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// =========================================================================
// EditUserModal — edit user profile fields.
// =========================================================================
function EditUserModal({ open, member, canWrite, onClose, onSubmit }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);

  React.useEffect(() => {
    if (open && member) {
      setForm({
        first_name:   member.first_name   || '',
        last_name:    member.last_name    || '',
        email:        member.email        || '',
        mobile:       member.mobile       || '',
        dob:          member.dob          ? member.dob.slice(0, 10) : '',
        joining_date: member.joining_date ? member.joining_date.slice(0, 10) : '',
        address:      member.address      || '',
        role:         member.role         || 'member',
      });
      setError(null);
      setSaving(false);
    }
  }, [open, member]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const todayISO = new Date().toISOString().slice(0, 10);
  const maxJoiningISO = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

  const handle = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const body = { ...form };
      // Send empty strings as null so backend uses COALESCE correctly
      Object.keys(body).forEach((k) => { if (body[k] === '') body[k] = null; });
      await onSubmit(body);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to save');
      setSaving(false);
    }
  };

  if (!member) return null;

  return (
    <Modal open={open} title="Edit User" onClose={saving ? undefined : onClose}>
      <form onSubmit={handle} className="entity-form">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <label className="form-label">
            First Name
            <input className="form-input" value={form.first_name || ''} onChange={set('first_name')} maxLength={100} />
          </label>
          <label className="form-label">
            Last Name
            <input className="form-input" value={form.last_name || ''} onChange={set('last_name')} maxLength={100} />
          </label>
        </div>

        {canWrite && !member.is_owner && (
          <label className="form-label">
            Access Role
            <select className="form-input" value={form.role || 'member'} onChange={set('role')}>
              <option value="admin">Admin</option>
              <option value="member">Member</option>
            </select>
          </label>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <label className="form-label">
            Email
            <input type="email" className="form-input" value={form.email || ''} onChange={set('email')} maxLength={120} />
          </label>
          <label className="form-label">
            Mobile
            <input
              className="form-input"
              value={form.mobile || ''}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                set('mobile')({ target: { value: val } });
              }}
              placeholder="10-digit number"
              maxLength={10}
            />
          </label>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <label className="form-label">
            Date of Birth
            <input type="date" className="form-input" value={form.dob || ''} onChange={set('dob')} min="1900-01-01" max={todayISO} />
          </label>
          <label className="form-label">
            Joining Date
            <input type="date" className="form-input" value={form.joining_date || ''} onChange={set('joining_date')} min="1970-01-01" max={maxJoiningISO} />
          </label>
        </div>

        <label className="form-label">
          Address
          <textarea className="form-input" rows={2} value={form.address || ''} onChange={set('address')} maxLength={500} />
        </label>

        {error && <div className="form-error">{error}</div>}
        <div className="form-actions">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </form>
    </Modal>
  );
}

// =========================================================================
// InviteModal (carried over from MembersAndInvites).
// =========================================================================
function InviteModal({ open, onClose, onSubmit }) {
  const [email, setEmail] = useState('');
  const [role, setRole]   = useState('member');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  React.useEffect(() => {
    if (open) { setEmail(''); setRole('member'); setError(null); setSubmitting(false); }
  }, [open]);

  const handle = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await onSubmit({ email: email.trim(), role });
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed');
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} title="Invite a person" onClose={submitting ? undefined : onClose}>
      <form onSubmit={handle} className="entity-form">
        <label className="form-label">
          Email
          <input
            type="email"
            className="form-input"
            value={email}
            onChange={(e) => setEmail(e.target.value.slice(0, 120))}
            maxLength={120}
            required
            autoFocus
          />
        </label>
        <label className="form-label">
          Access Role
          <select className="form-input" value={role} onChange={(e) => setRole(e.target.value)}>
            <option value="member">Member (read-only HR data; can create/comment on tasks)</option>
            <option value="admin">Admin (read + edit HR data)</option>
          </select>
          <small className="form-help">
            "Owner" is reserved for the person who created the app. Org-role
            assignments (CTO, EM, …) are made separately after the user
            accepts.
          </small>
        </label>
        {error && <div className="form-error">{error}</div>}
        <div className="form-actions">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={submitting}>
            {submitting ? 'Sending…' : 'Send Invitation'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
